import { useState, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { STATUSES, SECTORS, type Deal } from '@krit/core';
import { createDeal, getTeam, extractDeal, extractDealFromImages, uploadDocument, ocrDeckImages, type DeckTables, type DeckCoverage, type ExtractResult } from '../lib/api.js';
import { extractDeckText, renderPdfToImages, isLikelyScanned } from '../lib/pdf.js';

const num = (v: string | undefined) => (!v ? 0 : Number(v));

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <label className="block text-[11px] font-semibold text-muted mb-1">{label}</label>
      {children}
    </div>
  );
}
const inputCls = 'w-full px-2.5 py-2 border border-line rounded-lg text-[13px]';

export function NewDeal() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: teamData } = useQuery({ queryKey: ['team'], queryFn: getTeam, retry: false });
  const team = teamData?.team ?? [];

  const [f, setF] = useState<Record<string, string>>({
    company: '', short: '', sector: 'Auto Components', location: 'India', banker: 'Direct', poc: '', status: 'Open',
    rev26: '', rev25: '', pat26: '', pat25: '', ebitda26: '', valuationAsk: '', totalRaise: '', allocation: '',
    peNow: '', peIPO: '', ipo: 'TBD', de: '',
  });
  const set = (k: string, v: string) => setF((p) => ({ ...p, [k]: v }));

  // ── PDF autofill ────────────────────────────────────────────────────────
  // Scalars that aren't form inputs (narrative/profile/quality) + the sub-tables are
  // held here so they persist on create even though there's no input for each.
  const [extra, setExtra] = useState<{ fields: Record<string, string | number>; tables: DeckTables | null }>({ fields: {}, tables: null });
  const [cov, setCov] = useState<DeckCoverage | null>(null);
  const [pdf, setPdf] = useState<{ status: 'idle' | 'reading' | 'extracting' | 'done' | 'error'; msg: string }>({ status: 'idle', msg: '' });
  // Keep the uploaded deck so we can STORE it in Documents on create (so the deal has a
  // deck → the auto-populate chain fires on first open, same as a WhatsApp intake).
  const [deck, setDeck] = useState<{ file: File; text: string } | null>(null);
  async function onPdf(file: File) {
    try {
      setCov(null);
      setPdf({ status: 'reading', msg: `Reading ${file.name}…` });
      let text = await extractDeckText(file);
      // Scanned / image-only PDF (no text layer) → read it with a vision model instead.
      const isPdf = file.name.toLowerCase().endsWith('.pdf') || file.type === 'application/pdf';
      let visionExtract: ExtractResult | null = null;
      if (isPdf && isLikelyScanned(text)) {
        setPdf({ status: 'extracting', msg: 'Scanned deck — reading pages + tables with a vision model (~1 min)…' });
        const images = await renderPdfToImages(file);
        // Vision extraction reads the team/customer/product/P&L TABLES straight from the images;
        // OCR (in parallel) gives the stored deck text for later grounding.
        const [ve, ocr] = await Promise.all([
          extractDealFromImages(images),
          ocrDeckImages(images).catch(() => ({ text: '' })),
        ]);
        visionExtract = ve;
        if (ocr.text.trim()) text = ocr.text;
      }
      setDeck({ file, text });
      setPdf({ status: 'extracting', msg: 'Extracting deal details with AI (full deck — this can take ~20-40s)…' });
      const { fields, tables, coverage } = visionExtract ?? await extractDeal(text);
      setF((p) => {
        const next = { ...p };
        for (const [k, v] of Object.entries(fields)) if (k in p) next[k] = String(v);
        if (!next.short && next.company) next.short = next.company.replace(/[^A-Za-z]/g, '').slice(0, 4).toUpperCase();
        return next;
      });
      setExtra({ fields, tables });
      setCov(coverage);
      setPdf({ status: 'done', msg: `Pre-filled ${coverage.scalars.length} fields — review below, then Create.` });
    } catch (e) {
      setPdf({ status: 'error', msg: String((e as Error).message) });
    }
  }
  const busy = pdf.status === 'reading' || pdf.status === 'extracting';

  const mut = useMutation({
    mutationFn: async (body: Partial<Deal>) => {
      const res = await createDeal(body);
      // Store the deck PDF on the new deal (best-effort — the deal is created regardless).
      // This gives the deal a stored deck so the auto-populate chain fires when it's opened.
      if (deck) { try { await uploadDocument(res.deal.id, deck.file, deck.text, 0); } catch { /* deck store best-effort */ } }
      return res;
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['deals'] });
      navigate(`/deals/${res.deal.id}`);
    },
  });

  function submit() {
    if (!f.company || !f.short) return;
    // Non-form scalars pulled from the deck (narrative, profile, quality metrics).
    const extras: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(extra.fields)) if (!(k in f)) extras[k] = v;
    const t = extra.tables;
    mut.mutate({
      ...(extras as Partial<Deal>),
      company: f.company, short: f.short, sector: f.sector, location: f.location, banker: f.banker,
      poc: f.poc, status: f.status as Deal['status'],
      rev26: num(f.rev26), rev25: num(f.rev25), pat26: num(f.pat26), pat25: num(f.pat25), ebitda26: num(f.ebitda26),
      valuationAsk: num(f.valuationAsk), totalRaise: num(f.totalRaise), allocation: num(f.allocation),
      peNow: num(f.peNow) || 25, peIPO: num(f.peIPO) || 20, ipo: f.ipo, de: num(f.de),
      ...(t ? { team: t.team, customers: t.customers, products: t.products, pnl: t.pnl } : {}),
    });
  }

  return (
    <div className="max-w-[860px]">
      <div className="mb-4">
        <div className="text-[11px] text-muted mb-1.5">Sourcing</div>
        <h1 className="text-[21px] text-navy tracking-[-0.3px]">Add Deal</h1>
        <p className="text-[12.5px] text-muted mt-1">Core fields now; full detail (peers, team, diligence, score) editable on the deal record.</p>
      </div>

      {/* PDF autofill */}
      <div className="bg-card border border-line rounded-card shadow-sh p-4 mb-3 flex flex-wrap items-center gap-3">
        <label className={`px-3.5 py-2 rounded-lg text-[12.5px] font-semibold border cursor-pointer ${busy ? 'opacity-60 pointer-events-none' : ''} bg-navy text-white border-navy hover:opacity-90`}>
          {busy ? 'Working…' : '📄 Upload deck (PDF / PPTX) to autofill'}
          <input type="file" accept=".pdf,.pptx,application/pdf,application/vnd.openxmlformats-officedocument.presentationml.presentation" className="hidden" disabled={busy}
            onChange={(e) => { const file = e.target.files?.[0]; if (file) onPdf(file); e.target.value = ''; }} />
        </label>
        <span className={`text-[12px] ${pdf.status === 'error' ? 'text-red' : pdf.status === 'done' ? 'text-green' : 'text-muted'}`}>
          {pdf.msg || 'Upload a pitch deck and we’ll pre-fill the fields below for you to review.'}
        </span>
        {cov && (
          <div className="w-full mt-1 pt-3 border-t border-line-2 text-[11.5px]">
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-muted">
              <span>👥 Management: <b className="text-navy">{cov.team}</b></span>
              <span>🏢 Customers: <b className="text-navy">{cov.customers}</b></span>
              <span>📦 Products: <b className="text-navy">{cov.products}</b></span>
              <span>📈 P&amp;L years: <b className="text-navy">{cov.pnl}</b></span>
            </div>
            {cov.warnings.length > 0 && (
              <div className="mt-1.5 text-red font-semibold">⚠ {cov.warnings.join(' · ')}</div>
            )}
            {cov.missing.length > 0 && (
              <div className="mt-1.5 text-muted-2">Not stated in this deck (fill manually if needed): <span className="text-amber font-semibold">{cov.missing.join(', ')}</span></div>
            )}
            <div className="mt-1 text-muted-2">These populate the Team, Business &amp; Financials tabs once you create the deal.</div>
          </div>
        )}
      </div>

      <div className="bg-card border border-line rounded-card shadow-sh p-5">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
          <Field label="Company (full name) *"><input className={inputCls} value={f.company} onChange={(e) => set('company', e.target.value)} /></Field>
          <Field label="Short code *"><input className={inputCls} value={f.short} onChange={(e) => set('short', e.target.value)} /></Field>
          <Field label="Sector"><select className={inputCls} value={f.sector} onChange={(e) => set('sector', e.target.value)}>{Object.keys(SECTORS).map((s) => <option key={s}>{s}</option>)}</select></Field>
          <Field label="Location"><input className={inputCls} value={f.location} onChange={(e) => set('location', e.target.value)} /></Field>
          <Field label="Banker / MB"><input className={inputCls} value={f.banker} onChange={(e) => set('banker', e.target.value)} /></Field>
          <Field label="Krit POC"><select className={inputCls} value={f.poc} onChange={(e) => set('poc', e.target.value)}><option value="">—</option>{team.map((m) => <option key={m.id}>{m.name}</option>)}</select></Field>
          <Field label="Status"><select className={inputCls} value={f.status} onChange={(e) => set('status', e.target.value)}>{STATUSES.map((s) => <option key={s}>{s}</option>)}</select></Field>
          <Field label="IPO timeline"><input className={inputCls} value={f.ipo} onChange={(e) => set('ipo', e.target.value)} placeholder="e.g. FY28 · 18–24 mo" /></Field>
        </div>

        <h4 className="text-navy text-[13.5px] mt-5 mb-2">Financials (₹ Cr)</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3.5">
          <Field label="Revenue FY26"><input type="number" className={inputCls} value={f.rev26} onChange={(e) => set('rev26', e.target.value)} /></Field>
          <Field label="Revenue FY25"><input type="number" className={inputCls} value={f.rev25} onChange={(e) => set('rev25', e.target.value)} /></Field>
          <Field label="PAT FY26"><input type="number" className={inputCls} value={f.pat26} onChange={(e) => set('pat26', e.target.value)} /></Field>
          <Field label="PAT FY25"><input type="number" className={inputCls} value={f.pat25} onChange={(e) => set('pat25', e.target.value)} /></Field>
          <Field label="EBITDA FY26"><input type="number" className={inputCls} value={f.ebitda26} onChange={(e) => set('ebitda26', e.target.value)} /></Field>
          <Field label="Net D/E"><input type="number" className={inputCls} value={f.de} onChange={(e) => set('de', e.target.value)} /></Field>
        </div>

        <h4 className="text-navy text-[13.5px] mt-5 mb-2">Deal terms & valuation (₹ Cr)</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3.5">
          <Field label="Valuation ask"><input type="number" className={inputCls} value={f.valuationAsk} onChange={(e) => set('valuationAsk', e.target.value)} /></Field>
          <Field label="Total round"><input type="number" className={inputCls} value={f.totalRaise} onChange={(e) => set('totalRaise', e.target.value)} /></Field>
          <Field label="Allocation to us"><input type="number" className={inputCls} value={f.allocation} onChange={(e) => set('allocation', e.target.value)} /></Field>
          <Field label="Peer P/E now"><input type="number" className={inputCls} value={f.peNow} onChange={(e) => set('peNow', e.target.value)} /></Field>
          <Field label="Peer P/E @IPO"><input type="number" className={inputCls} value={f.peIPO} onChange={(e) => set('peIPO', e.target.value)} /></Field>
        </div>

        {mut.isError && <p className="text-red text-[12px] mt-3">{String((mut.error as Error).message)}</p>}

        <div className="flex gap-2 mt-5">
          <button onClick={submit} disabled={mut.isPending || !f.company || !f.short} className="px-4 py-2 rounded-lg text-[12.5px] font-semibold bg-teal text-white hover:bg-teal-d disabled:opacity-50">
            {mut.isPending ? 'Saving…' : 'Create deal'}
          </button>
          <button onClick={() => navigate('/deals')} className="px-4 py-2 rounded-lg text-[12.5px] font-semibold bg-white text-navy border border-line">Cancel</button>
        </div>
      </div>
    </div>
  );
}
